import router from '@/router';
import { pickBy } from 'lodash';
import { RouteLocationRaw } from 'vue-router';
import { CaseCluster, CaseParticipant } from '@/api';
import { PagePath, PagePaths, PageState, PageTypes } from '../application/page.definitions';
import { getMsbStateFromUrl } from '../multisidebar/helpers';

export const ExcludedFilterFields: Array<string> = ['limit'];
export const ExportedPageStateFields: Array<keyof PageState> = [
  'pageType',
  'objectType',
  'cardType',
  'episodeType',
  'id',
  'searchFrom',
  'subPage',
  'additionalId'
];

export type IRouteOptions = {
  path: PagePath;
  id?: any;
  filter?: any;
  tab?: any;
  pageState?: Partial<PageState>;
};

export class RouterModule {
  static pageStateToQuery(value: Partial<PageState>): Record<string, any> {
    const filter = value?.filter || {};
    const exportedPageStateFields = pickBy(value, (v: any, k: any) => ExportedPageStateFields.indexOf(k) > -1 && checkHasValue(v));
    const exportedFilterFields = pickBy(filter, (v: any, k: any) => ExcludedFilterFields.indexOf(k) === -1 && checkHasValue(v));
    const hasFilter = Object.keys(exportedFilterFields).length;
    if (hasFilter) exportedPageStateFields.filter = exportedFilterFields;

    const msbState = getMsbStateFromUrl(window.location.href);
    
    const query: any = Object.assign({}, router.currentRoute.value.query, {
      tab: value.tab,
      msbState: JSON.stringify(msbState),
      pageState: Object.keys(exportedPageStateFields).length ? JSON.stringify(exportedPageStateFields) : null
    });

    return query;
  }

  static queryToPageState(query: Record<string, any>): Partial<PageState> {
    const result: Partial<PageState> = { tab: query.tab, id: query.id };
    if (query?.pageState) Object.assign(result, JSON.parse(query.pageState as string));
    return result;
  }

  static getRouteLocation(options: IRouteOptions): RouteLocationRaw {
    const tab = options.tab ?? getRandomId();
    const pageState: Partial<PageState> = options.pageState || { id: options.id };
    pageState.tab = tab;
    return {
      path: options.path,
      query: this.pageStateToQuery(pageState)
    };
  }

  static navigateToCard(id: number, type: string): void {
    const location = RouterModule.getRouteLocation({ path: PagePaths.Cards, pageState: { cardType: type, id } });
    router.push(location);
  }

  static navigateToInteractions(filter: Record<string, any>): void {
    const location = RouterModule.getRouteLocation({
      path: PagePaths.Interactions,
      pageState: { pageType: PageTypes.Clusters, objectType: 'faces', filter }
    });
    router.push(location);
  }

  static navigateToUser(id: number): void {
    const location = RouterModule.getRouteLocation({ path: PagePaths.Users, pageState: { id } });
    router.push(location);
  }

  static navigateToCase(id: number, pageStateOptions?: any): void {
    const location = RouterModule.getRouteLocation({ path: PagePaths.Case, pageState: { id, ...pageStateOptions } });
    router.push(location);
  }

  static navigateToClusters(item: CaseCluster): void {
    const location = RouterModule.getRouteLocation({
      path: PagePaths.Case,
      pageState: { pageType: 'clusters', id: item.case, additionalId: item.id }
    });
    router.push(location);
  }

  static navigateToRole(id: number): void {
    const location = RouterModule.getRouteLocation({ path: PagePaths.Roles, pageState: { id } });
    router.push(location);
  }

  static navigateToEpisode(id: number, type: string): void {
    const location = RouterModule.getRouteLocation({ path: PagePaths.Events, pageState: { pageType: PageTypes.Episodes, episodeType: type, id } });
    router.push(location);
  }

  static navigateToEvents(type: string, filter: any): void {
    const location = RouterModule.getRouteLocation({
      path: PagePaths.Events,
      pageState: { pageType: PageTypes.Events, objectType: type, filter }
    });
    router.push(location);
  }

  static navigateToWatchList(id: number): void {
    const location = RouterModule.getRouteLocation({ path: PagePaths.WatchLists, pageState: { id } });
    router.push(location);
  }

  static navigateToCameraGroup(id: number): void {
    const location = RouterModule.getRouteLocation({ path: PagePaths.CameraGroups, pageState: { id } });
    router.push(location);
  }

  static navigateToExternalSearch(id: number): void {
    const location = RouterModule.getRouteLocation({ path: PagePaths.ExternalSearch, pageState: { id } });
    router.push(location);
  }
}

function checkHasValue(v: any): boolean {
  return Array.isArray(v) ? !!v.length : v === false || v === 0 ? true : !!v;
}

export function getRandomId() {
  return Math.round(Math.random() * Number.MAX_SAFE_INTEGER).toString(36);
}
